import {
  IconAddressBook,
  IconArrowsExchange,
  IconBook,
  IconBook2,
  IconBuildingStore,
  IconCash,
  IconChartBar,
  IconChartLine,
  IconClipboardList,
  IconCreditCard,
  IconDeviceDesktop,
  IconFileExport,
  IconId,
  IconLayoutDashboard,
  IconListNumbers,
  IconLock,
  IconNotebook,
  IconPackages,
  IconPencil,
  IconPercentage,
  IconQrcode,
  IconReceipt,
  IconReportSearch,
  IconTicket,
  IconTimeline,
  IconTruckDelivery,
  IconUserCheck,
  IconUsers,
  type TablerIcon,
} from '@tabler/icons-react';
import type { ReactNode } from 'react';

import {
  type AccessDeclaration,
  publicRoute,
  requireRole,
  requireSignedIn,
  signedOutOnly,
} from './access';
import { BillingMachine } from './billing-machines/BillingMachine';
import { BillingMachines } from './billing-machines/BillingMachines';
import { ProductCodes } from './billing-machines/ProductCodes';
import { Catalogue } from './catalogue/Catalogue';
import { CatalogueUpload } from './catalogue/CatalogueUpload';
import { ItemAvailability } from './catalogue/ItemAvailability';
import { OutletCatalogue } from './catalogue/OutletCatalogue';
import { PriceHistory } from './catalogue/PriceHistory';
import { ChangeCredential } from './ChangeCredential';
import { DailySheet } from './daily-sheet/DailySheet';
import { OpeningBalance } from './daily-sheet/OpeningBalance';
import { SheetLayout } from './daily-sheet/SheetLayout';
import { dayCloseAccess } from './day-close/access';
import { DayClose } from './day-close/DayClose';
import { MachineBill } from './machine-bills/MachineBill';
import { MachineBills } from './machine-bills/MachineBills';
import { MachineImport } from './machine-uploads/MachineImport';
import { MachineUpload } from './machine-uploads/MachineUpload';
import { MachineUploads } from './machine-uploads/MachineUploads';
import { NotFound } from './NotFound';
import { Outlets } from './outlets/Outlets';
import { paths } from './paths';
import { Preview } from './Preview';
import { queuedWritesAccess } from './queued-writes/access';
import { QueuedWrites } from './queued-writes/QueuedWrites';
import { Attribution } from './reporting/Attribution';
import { CashBook } from './reporting/CashBook';
import { Expenses } from './reporting/Expenses';
import { ItemExport } from './reporting/ItemExport';
import { Items } from './reporting/Items';
import { ItemSales } from './reporting/ItemSales';
import { PaymentModes } from './reporting/PaymentModes';
import { ProfitAndLoss } from './reporting/ProfitAndLoss';
import { RecordExpense } from './reporting/RecordExpense';
import { Trace } from './reporting/Trace';
import { TradingSummary } from './reporting/TradingSummary';
import { RevenueShares } from './revenue-shares/RevenueShares';
import { Root } from './Root';
import { auditRouteTable } from './route-audit';
import { Sale } from './sales/Sale';
import { Sales } from './sales/Sales';
import { SignIn } from './SignIn';
import { Staffing } from './staffing/Staffing';
import { OutletStock } from './stock/OutletStock';
import { RecordCorrection } from './stock/RecordCorrection';
import { RecordDelivery } from './stock/RecordDelivery';
import { RecordStocktake } from './stock/RecordStocktake';
import { StockMovements } from './stock/StockMovements';
import { TaxIdentities } from './tax-identities/TaxIdentities';
import { Terminals } from './terminals/Terminals';
import { Ticket } from './tickets/Ticket';
import { Tickets } from './tickets/Tickets';
import { Till } from './till/Till';
import { UpiDeclarations } from './upi-declarations/UpiDeclarations';
import { Users } from './users/Users';
import { Vendors } from './vendors/Vendors';

/**
 * Every route in this app, as data, each with the declaration that says who may
 * see it.
 *
 * The table is a list rather than a tree of `<Route>` elements for one reason:
 * something has to be able to walk it. The API's boot audit walks its own route
 * table and refuses to start on an undeclared route; a table written as JSX can
 * only be walked by rendering it, which is exactly too late. Written as data it
 * is audited at import (`route-audit.ts`), guarded at render (`RouteGuard.tsx`)
 * and filtered into navigation (`navigation.ts`) — three readers of one list,
 * none of which can drift from the others.
 *
 * `App.tsx` renders this and contains no `<Route>` of its own. That is what
 * makes "forgot to wrap the route in its guard" an impossible mistake rather
 * than a likely one: the wrapping is done once, by the renderer, for every
 * entry. The remaining mistake — forgetting to *declare* — is caught by the
 * required `access` field, by the audit, and by the guard, in that order.
 */
/**
 * The sections the navigation margin draws, in the order it draws them.
 *
 * The park's own vocabulary rather than an invented taxonomy — these are the
 * words used for the work, not names for a menu. The order here is documentary:
 * the margin takes its order from `nav.order` alone, and the sections fall out
 * of it because the items of a section are contiguous in that sequence.
 * Renaming one is a change to what the staff read, so it belongs here where it
 * can be read once, and not in the shell.
 */
export type NavGroup = 'SETUP' | 'SELLING' | 'STOCK' | 'MONEY' | 'REPORTS';

export interface RouteDefinition {
  /** As react-router matches it. `*` is the catch-all. */
  readonly path: string;

  /** What renders when the declaration admits the viewer. */
  readonly element: ReactNode;

  /**
   * Who may see it. Required, so a new route cannot compile without an answer.
   *
   * This is the whole ticket in one field. Everything else — the audit, the
   * guard, the navigation filter — exists because a required field is not
   * enough on its own: a cast satisfies it, and a type does not survive into
   * the running app.
   */
  readonly access: AccessDeclaration;

  /**
   * Where this route appears in the back-office navigation, if it does.
   *
   * Absent means "reachable, but not advertised" — the sign-in screen, the
   * not-found screen. Navigation is built by filtering this table through the
   * same `access` the guard reads, so a link can never be shown for a route the
   * guard would refuse. Hiding the link is a courtesy either way; what this
   * removes is the second list that would otherwise drift out of step with the
   * first.
   *
   * `group` and `icon` are here for the same reason the label is: the margin
   * (`BackOfficeShell`) draws whatever this table says, and a second list
   * holding the sections or the icons would be a second place to forget one.
   * `icon` is required, so a route cannot join the navigation without an
   * answer; `group` is optional, because Overview stands alone above the
   * sections.
   */
  readonly nav?: {
    readonly label: string;
    readonly order: number;
    readonly group?: NavGroup;
    readonly icon: TablerIcon;
  };
}

export const routeTable: readonly RouteDefinition[] = [
  {
    path: paths.dashboard,
    element: <Root />,
    // The signed-in landing screen. Any role may see it; what it shows them is
    // the navigation their role can reach.
    access: requireSignedIn(),
    nav: { label: 'Overview', order: 10, icon: IconLayoutDashboard },
  },

  {
    path: paths.signIn,
    element: <SignIn />,
    // Public in the API's document too — the sign-in operation's `security` is
    // `[]` — and `signedOut` rather than `public` because somebody who already
    // has a session has nothing to do here.
    access: signedOutOnly(),
  },

  {
    path: paths.changeCredential,
    element: <ChangeCredential />,
    // The one route the API answers while `mustChangeCredential` is set, and so
    // the one route that must admit somebody in that state — declared any other
    // way, the guard sends them here and this declaration turns them round, for
    // ever. The start-up audit refuses exactly that shape by name.
    //
    // `requireSignedIn` and not a role: every role can be provisioned with a
    // credential somebody else chose, and every role has to be able to replace
    // it. It admits people whose flag is already false too, which is correct —
    // changing your own password is not a privilege reserved for the day you
    // were handed one.
    //
    // No `nav` entry. Somebody sent here has no navigation drawn at all (every
    // other route answers `must-change-credential`, so `navigationFor` filters
    // them out), and for everybody else a permanent "Change your credential"
    // link in the back-office header is a menu item for a once-a-year task. A
    // profile or account screen is where it belongs, and neither exists yet.
    access: requireSignedIn({ allowsPendingCredential: true }),
  },

  {
    path: paths.outlets,
    element: <Outlets />,
    // `OWNER` and nothing wider — deliberately **stricter** than the API, which
    // is not what an earlier version of this comment claimed.
    //
    // `outlets.controller.ts` puts `@RequireRole('OWNER')` on the class and two
    // handlers override it on purpose, citing FR-111:
    //
    //   GET /v1/outlets        `@RequireRole()` + `@RequireOutletScope()`
    //   GET /v1/outlets/{id}   `@RequireRole()` + `@RequireOutletScope()`
    //   PATCH /v1/outlets/{id} `@RequireRole('OWNER')` + `@RequireOutletScope()`
    //   DELETE                 `@RequireRole('OWNER')` + `@RequireOutletScope()`
    //   POST /v1/outlets       the class's `@RequireRole('OWNER')`, inherited
    //
    // So the two reads admit any authenticated caller, scoped to their own
    // outlets, and only the writes are owner-only. FE008's ticket says "both are
    // OWNER-only", the controller says otherwise, and `blossom-rest-api` issue
    // 135 is open asking which is authoritative. Until that is answered the
    // declaration stays narrow: a guard stricter than the server refuses people
    // who would have been let in, which is recoverable, and the other way round
    // is a screen that renders and then 403s.
    //
    // Do not widen this to match the controller without that answer, and do not
    // read the narrowness as an accident.
    //
    // The `nav` entry is filtered through this same declaration
    // (`navigation.ts`), so a manager is not shown a link to it. That is
    // courtesy and not security — the role comes from `sessionStorage` and is
    // editable — and the screen still handles `AUTH_FORBIDDEN` arriving.
    access: requireRole('OWNER'),
    nav: { label: 'Outlets', order: 20, group: 'SETUP', icon: IconBuildingStore },
  },

  {
    path: paths.terminals,
    element: <Terminals />,
    // `OWNER` and nothing wider, mirroring the API: `TerminalsController`
    // carries `@RequireRole('OWNER')` on the class, so the list, the create and
    // the revoke behind this screen all refuse anybody else. A manager admitted
    // here would get a screen whose every call 403s.
    access: requireRole('OWNER'),
    nav: { label: 'Terminals', order: 30, group: 'SETUP', icon: IconDeviceDesktop },
  },

  {
    path: paths.billingMachines,
    element: <BillingMachines />,
    // `OWNER` and `MANAGER`, mirroring the API's reads. Registering a machine is
    // owner-only there, so the screen hides that control from a manager and
    // still words the `AUTH_FORBIDDEN` if it arrives.
    //
    // No `nav` entry, on purpose: the billing machines were taken out of service
    // on 4 Nov 2026 and every outlet sells on the till (#346). The route stays so
    // a bookmarked URL still opens. Putting it back is the one `nav` line #346 took out.
    access: requireRole('OWNER', 'MANAGER'),
  },

  {
    path: paths.billingMachine,
    element: <BillingMachine />,
    // The same declaration as the list it is reached from. Moving the machine is
    // owner-only on the API and hidden from a manager on the screen. No `nav`
    // entry, as the list has none either (#346).
    access: requireRole('OWNER', 'MANAGER'),
  },

  {
    path: paths.billingMachineProductCodes,
    element: <ProductCodes />,
    // `OWNER` and `MANAGER` for the read and for the write alike — mapping a
    // code is open to both on the API, so nothing on this screen is hidden.
    access: requireRole('OWNER', 'MANAGER'),
  },

  {
    path: paths.users,
    element: <Users />,
    // `OWNER` and nothing wider, mirroring the API: every operation on
    // `/v1/users` carries `@RequireRole('OWNER')`, and CL-104 is explicit that
    // the manager role does not manage people. The outlet roster this screen
    // also reads (`GET /v1/outlets/{id}/users`) is `OWNER`-only too, so there is
    // no wider declaration that would leave the screen half-working.
    //
    // The `nav` entry is filtered through this same declaration
    // (`navigation.ts`), so a manager is not shown a link to it. That is
    // courtesy and not security — the role comes from `sessionStorage` and is
    // editable — and the screen still handles `AUTH_FORBIDDEN` arriving.
    //
    // `order: 40` and not the 30 this entry carried before the rebase: FE010
    // landed Terminals on 30 first. `navigationFor` sorts on this number alone,
    // so two entries sharing one leaves their order down to `sort` stability —
    // which is a coin toss to read and a link that moves under somebody's
    // pointer between builds. Changed here rather than on the terminals entry
    // because this is the one that arrived second.
    access: requireRole('OWNER'),
    nav: { label: 'People', order: 40, group: 'SETUP', icon: IconUsers },
  },

  {
    path: paths.staffing,
    element: <Staffing />,
    // `OWNER` and nothing wider, mirroring the API: `UserAssignmentsController`
    // and `OutletAssignmentsController` both carry `@RequireRole('OWNER')` on
    // the class, and the `PUT` behind this screen documents the refusal in as
    // many words — "AUTH_FORBIDDEN — only the owner staffs the park". A manager
    // admitted here would get a screen whose every call 403s.
    //
    // Note that this is *narrower than the reach the screen talks about*: a
    // manager reaches every outlet's records by role (FR-111), and cannot staff
    // anybody. The two are different questions and the declaration answers the
    // second one.
    //
    // `order: 50`, after People at 40 — you pick a person there and staff them
    // here, and `navigationFor` sorts on this number alone, so two entries
    // sharing one leaves their order to `sort` stability.
    access: requireRole('OWNER'),
    nav: { label: 'Staffing', order: 50, group: 'SETUP', icon: IconUserCheck },
  },

  {
    path: paths.catalogue,
    element: <Catalogue />,
    // `OWNER` and `MANAGER` — the first back-office route that is not
    // owner-only, and it mirrors the API rather than relaxing anything.
    //
    // `CatalogueItemsController` carries `@RequireRole('OWNER')` on the class,
    // and `list` overrides it with `@RequireRole()` + `@RequireOutletScope()`
    // *and says why*: FR-232's second sentence is that a manager must be able
    // to read the catalogue, and outlet and gate staff read what is sellable
    // where they work. This is not the ambiguity FE008 hit on outlets, where
    // the ticket and the controller disagreed and `blossom-rest-api#135` is
    // still open — here the controller argues the case in its own comment and
    // the ticket agrees, spending a whole section on what a *manager* sees.
    //
    // Stopping at manager rather than following the API all the way to
    // `requireSignedIn()`: outlet and gate staff sign in with a PIN at a till,
    // and the back-office shell is not their shell. Widening this to admit them
    // is a decision about the till's navigation and belongs to the ticket that
    // builds one, not to this screen. Narrower is the recoverable direction.
    //
    // Creating an item *is* owner-only — every write on that controller
    // inherits the class declaration — so the "New item" button is hidden for a
    // manager rather than shown and refused. Hiding it is courtesy: the role
    // comes from `sessionStorage` and is editable, and the server refuses
    // regardless.
    access: requireRole('OWNER', 'MANAGER'),
    nav: { label: 'Catalogue', order: 60, group: 'SETUP', icon: IconBook },
  },

  {
    path: paths.catalogueUpload,
    element: <CatalogueUpload />,
    // `OWNER` and nothing wider — **narrower than the catalogue list above it**,
    // and mirroring the write rather than the read.
    //
    // Every request this screen makes is `POST /v1/catalogue/items`, which
    // inherits `CatalogueItemsController`'s class-level `@RequireRole('OWNER')`
    // and states the reason on the operation itself: "AUTH_FORBIDDEN — only the
    // owner stocks the catalogue (CL-206)". The two reads on that controller
    // step out to admit a manager; nothing this screen does is one of them, so
    // a manager admitted here would get a review of five hundred rows and a
    // button that 403s on the first of them.
    //
    // No `nav` entry: it is an action on the catalogue and not a destination.
    // It is reached from the Catalogue screen, where the button is shown to an
    // owner alone — courtesy, not security, exactly as the "New item" button
    // beside it. `NavLink` matches descendants by default, so Catalogue stays
    // lit while this is open, which is where somebody on it actually is.
    access: requireRole('OWNER'),
  },

  {
    path: paths.cataloguePrices,
    element: <PriceHistory />,
    // `OWNER` and nothing wider — **narrower than the catalogue list above it**,
    // and mirroring the API exactly rather than tightening anything.
    //
    // `CataloguePricesController` carries `@RequireRole('OWNER')` on the class
    // and no handler steps out of it, unlike the items controller where `list`
    // and `read` do. The document states the reason on all three operations:
    // "cost price is the owner's alone (CL-207, FR-233)". Every row of this
    // history carries `costPaise`, so there is no version of this screen with a
    // column removed that a manager could usefully open — the reads themselves
    // are refused, and the screen would be four skeletons and a 403.
    //
    // The guard therefore belongs here rather than on fields inside a screen
    // anybody can reach. A manager who edits their role in `sessionStorage` gets
    // past this line and meets the API's refusal instead, which the screen
    // answers as a boundary rather than as an error.
    //
    // No `nav` entry: it is about *one* item, reached from that item's amount in
    // the catalogue list, and there is no such thing as the price history in
    // general. `NavLink` matches descendants by default, so the Catalogue item
    // stays lit while this screen is open — which is where somebody on it
    // actually is.
    access: requireRole('OWNER'),
  },

  {
    path: paths.catalogueAvailability,
    element: <ItemAvailability />,
    // `OWNER` and nothing wider — **narrower than the catalogue list**, and
    // mirroring the write rather than the read.
    //
    // `CatalogueAvailabilityController` carries `@RequireRole('OWNER')` on the
    // class and states the reason on the `PUT` in as many words: "only the owner
    // sets availability (CL-206)". The `GET` beside it steps out to
    // `@RequireRole()` + `@RequireOutletScope()` so a till can read what it
    // sells — but this screen is the write. Every control on it sets the set, so
    // a manager admitted here would get a table of checkboxes and a Save that
    // 403s. The reverse direction below is where a manager reads availability,
    // and the screen says so when the refusal arrives anyway.
    //
    // No `nav` entry: it is about *one* item, reached from that item in the
    // catalogue and from the outlet catalogue's rows. `NavLink` matches
    // descendants, so Catalogue stays lit while this is open — which is where
    // somebody on it actually is.
    access: requireRole('OWNER'),
  },

  {
    path: paths.outletCatalogue,
    element: <OutletCatalogue />,
    // `OWNER` and `MANAGER`, matching the catalogue list rather than the outlets
    // list this hangs off. `OutletCatalogueController.read` is `@RequireRole()`
    // + `@RequireOutletScope()`, so the API admits outlet and gate staff to
    // their own outlets as well; this stops at manager for the reason the
    // catalogue entry gives — till staff have their own shell, and widening the
    // back office to them belongs to the ticket that builds one.
    //
    // Deliberately **wider** than `paths.outlets`, which is `OWNER`-only and is
    // not the way here: a manager reaches this from the catalogue's "Sold at"
    // filter. The two answer different questions — administering the park's
    // outlets, and reading what one of them sells — and FR-232's second sentence
    // is that a manager must be able to read the catalogue.
    //
    // No `nav` entry: it is about *one* outlet.
    access: requireRole('OWNER', 'MANAGER'),
  },

  {
    path: paths.taxIdentities,
    element: <TaxIdentities />,
    // `OWNER` and `MANAGER`, mirroring `GET /v1/tax-identities`. Saving is
    // `PUT /v1/tax-identities/{id}`, owner only, so a manager is shown the
    // same registrations with nothing to edit — the screen decides that, and
    // the API refuses regardless.
    //
    // `order: 65`, the last of SETUP after Catalogue at 60: it is what a taxed
    // item's invoice prints above its lines.
    access: requireRole('OWNER', 'MANAGER'),
    nav: { label: 'Tax identities', order: 65, group: 'SETUP', icon: IconId },
  },

  {
    path: paths.tickets,
    element: <Tickets />,
    // `OWNER` and `MANAGER`, matching the catalogue list rather than the
    // owner-only setup screens, and mirroring the API without relaxing it.
    //
    // `TicketsController` declares `@RequireRole()` — any authenticated caller —
    // and `@RequireOutletScope()` on all three of its reads, and argues the case
    // in its own comment: FR-322 is about *reach* rather than rank, so an owner
    // and a manager see every outlet and outlet and gate staff see their
    // assignments, expressed as a conjunct of the `WHERE` clause rather than as a
    // list of roles. Naming the four roles there would have to be revisited the
    // day a fifth is added.
    //
    // Stopping at manager rather than following the API all the way to
    // `requireSignedIn()`, for the reason the catalogue entry gives: outlet and
    // gate staff sign in with a PIN at a till, and the back-office shell is not
    // their shell. Widening this to admit them is a decision about the till's
    // navigation and belongs to the ticket that builds one. Narrower is the
    // recoverable direction.
    //
    // The screen writes nothing, so there is no control here to hide from a
    // manager — but two of its *filters* are hidden from one, because the device
    // and staff directories that fill them are owner-only on the API. That is
    // recorded on the screen itself.
    access: requireRole('OWNER', 'MANAGER'),
    nav: { label: 'Tickets', order: 70, group: 'SELLING', icon: IconTicket },
  },

  {
    path: paths.ticket,
    element: <Ticket />,
    // `OWNER` and `MANAGER`, the same declaration as the list above it and for
    // the same reasons: `TicketsController` declares `@RequireRole()` and
    // `@RequireOutletScope()` on all three of its reads — the list, the read by
    // id and the read by number — so one screen's worth of reach is exactly the
    // other's, expressed as a conjunct of the `WHERE` clause rather than as a
    // list of roles.
    //
    // **Deliberately not wider than the list.** This screen is reached from it
    // and from a lookup on it, and a record that admitted somebody the list
    // refuses would be a way round a guard rather than a screen. The API's own
    // answer for a ticket outside the caller's reach is a `404` identical to the
    // one for a ticket nobody printed (FR-128), so the boundary holds either
    // way — this keeps the two declarations from drifting apart, which is the
    // thing nothing would catch.
    //
    // **This screen writes, and its one write is more permissive than this
    // guard.** `POST /v1/tickets/{id}/void` declares `@RequireRole()` with no
    // arguments — anyone signed in who can reach the ticket may void it, because
    // the rule is reach rather than rank — so a cashier the API would accept is
    // refused here by the *route*, not by the control on it. That is deliberate
    // and is the note above restated: the record is not wider than the list it
    // is reached from, and the list is a back-office screen. Nothing inside the
    // record checks a role, and nothing should; widening this is one edit here
    // on the day a till-side ticket lookup exists to be reached from.
    //
    // No `nav` entry — it is about *one* ticket. `NavLink` matches descendants
    // by default, so Tickets stays lit while this screen is open, which is where
    // somebody on it actually is.
    access: requireRole('OWNER', 'MANAGER'),
  },

  {
    path: paths.sales,
    element: <Sales />,
    // `OWNER` and `MANAGER`, the same declaration as the tickets list beside it
    // and mirroring the API without relaxing it.
    //
    // `SalesController` declares `@RequireRole()` — any authenticated caller —
    // and `@RequireOutletScope()` on all three of its reads, and argues the case
    // in its own comment (R-412): reach is about *assignment* rather than rank,
    // so an owner and a manager see every outlet and outlet staff see theirs,
    // expressed as a conjunct of the `WHERE` clause rather than as a list of
    // roles. Naming the roles there would have to be revisited the day a fifth
    // is added.
    //
    // Stopping at manager rather than following the API all the way to
    // `requireSignedIn()`, for the reason the tickets entry gives: outlet and
    // gate staff sign in with a PIN at a till, and the back-office shell is not
    // their shell. Widening this to admit them is a decision about the till's
    // navigation and belongs to the ticket that builds one. Narrower is the
    // recoverable direction.
    //
    // The screen writes nothing — refund and reprint are FE034 — so there is no
    // control here to hide from a manager. Two of its *filters* are hidden from
    // one, because the device and staff directories that fill them are
    // owner-only on the API. That is recorded on the screen itself.
    access: requireRole('OWNER', 'MANAGER'),
    nav: { label: 'Sales', order: 80, group: 'SELLING', icon: IconReceipt },
  },

  {
    path: paths.sale,
    element: <Sale />,
    // `OWNER` and `MANAGER`, the same declaration as the list above it and for the
    // same reasons: `SalesController` declares `@RequireRole()` and
    // `@RequireOutletScope()` on all three of its reads — the list, the read by id
    // and the read by number — so one screen's worth of reach is exactly the
    // other's, expressed as a conjunct of the `WHERE` clause rather than as a list
    // of roles.
    //
    // **Deliberately not wider than the list.** This screen is reached from it and
    // from a lookup on it, and a record that admitted somebody the list refuses
    // would be a way round a guard rather than a screen. The API's own answer for
    // a sale outside the caller's reach is a `404` identical to the one for a sale
    // nobody rang up (FR-437, FR-128), so the boundary holds either way — this
    // keeps the two declarations from drifting apart, which is the thing nothing
    // would catch.
    //
    // **This screen writes twice, and both writes are more permissive than this
    // guard.** `POST /v1/sales/{id}/refund` and `POST /v1/sales/{id}/reprint`
    // each declare `@RequireRole()` with no arguments and
    // `@RequireOutletScope()` — anyone signed in who can reach the sale may
    // refund it or reprint its receipt, because the rule is reach rather than
    // rank (CL-403, FR-414b) — so a cashier the API would accept is refused here
    // by the *route*, not by the controls on it. That is deliberate and is the
    // note above restated: the record is not wider than the list it is reached
    // from, and the list is a back-office screen. Nothing inside the record
    // checks a role, and nothing should; widening this is one edit here on the
    // day a till-side sale lookup exists to be reached from.
    //
    // Neither write mutates the sale. A refund is a new row plus a reversing
    // entry in the books (R-408) and a reprint changes nothing at all beyond an
    // audit row, so "a recorded sale is never edited" is still true of every
    // control on this screen.
    //
    // No `nav` entry — it is about *one* sale. `NavLink` matches descendants by
    // default, so Sales stays lit while this screen is open, which is where
    // somebody on it actually is.
    access: requireRole('OWNER', 'MANAGER'),
  },

  {
    path: paths.machineUploads,
    element: <MachineUploads />,
    // `OWNER` and `MANAGER`, mirroring `MachineImportsController` on all five of
    // its routes — the list, the read, the file, the preview and the upload.
    //
    // No `nav` entry, on purpose: the billing machines were taken out of service
    // on 4 Nov 2026 and every outlet sells on the till (#346). The route stays so
    // a bookmarked URL still opens. Putting it back is the one `nav` line #346 took out.
    access: requireRole('OWNER', 'MANAGER'),
  },

  {
    path: paths.machineUpload,
    element: <MachineUpload />,
    // The same declaration as the list it is reached from. No `nav` entry.
    access: requireRole('OWNER', 'MANAGER'),
  },

  {
    path: paths.machineImport,
    element: <MachineImport />,
    // The same declaration again. No `nav` entry, as the list has none either (#346).
    access: requireRole('OWNER', 'MANAGER'),
  },

  {
    path: paths.machineBills,
    element: <MachineBills />,
    // `OWNER` and `MANAGER`, mirroring `MachineBillsController` on all three of
    // its routes — the list, the read and the reversal. Both roles reach every
    // outlet (FR-111 as amended), so nothing on the screen is hidden from a
    // manager.
    //
    // No `nav` entry, on purpose: the billing machines were taken out of service
    // on 4 Nov 2026 and every outlet sells on the till (#346). The route stays so
    // a bookmarked URL still opens. Putting it back is the one `nav` line #346 took out.
    access: requireRole('OWNER', 'MANAGER'),
  },

  {
    path: paths.machineBill,
    element: <MachineBill />,
    // The same declaration as the list it is reached from, and the reversal on
    // it is `OWNER` and `MANAGER` too. No `nav` entry, as the list has none either (#346).
    access: requireRole('OWNER', 'MANAGER'),
  },

  {
    path: paths.stock,
    element: <OutletStock />,
    // **`requireSignedIn()`, and the widest declaration in this table after the
    // dashboard.** It mirrors the API exactly and is worth reading before it is
    // copied or tightened.
    //
    // `StockLevelsController.read` declares `@RequireRole()` with no arguments
    // and `@RequireOutletScope()`. So does the movement log beside it. The three
    // routes that *change* a level — deliveries, corrections, stocktakes — carry
    // `@RequireRole('OWNER', 'MANAGER')`, and none of them is on this screen:
    // nothing here writes, and each of those is its own ticket with its own
    // guard to declare.
    //
    // Every other back-office read in this table stops at manager, citing the
    // same sentence — till staff sign in with a PIN at a counter and this is not
    // their shell — and that reasoning is deliberately **not** followed here.
    // Those screens are administration end to end: the day's sales, the park's
    // catalogue, who works where. This one is a shelf count, and the epic ships
    // no till screen for it at all (terminals move stock as a side effect of
    // selling, and a cashier never records any). A cashier who wants to know
    // whether the doll shop has any dolls left has this screen or nothing, and a
    // guard stricter than the server is a screen hidden from somebody the API
    // would have served.
    //
    // "Narrower is the recoverable direction" still holds and still argues for
    // stopping at manager everywhere else. It does not argue for stopping here,
    // because here there is no second screen the refused person could use.
    //
    // What outlets somebody sees is not this line's business either way:
    // `@RequireOutletScope()` is on the route, so an owner and a manager reach
    // every outlet by role (FR-111) and everybody else reaches their
    // assignments. Where that bites is the outlet picker, which is filled from a
    // list scoped the same way, and a 404 for anything outside it — which the
    // screen answers as "there is no such outlet", in those words, because the
    // API refuses to distinguish the two.
    access: requireSignedIn(),
    nav: { label: 'Stock', order: 90, group: 'STOCK', icon: IconPackages },
  },

  {
    path: paths.stockMovements,
    element: <StockMovements />,
    // `requireSignedIn()`, for exactly the reasons the entry above sets out and
    // by the same mirror: `StockMovementsController.list` declares
    // `@RequireRole()` with no arguments and `@RequireOutletScope()`, so reach
    // is a conjunct of the query rather than a rank. Read that entry before
    // tightening this one — the argument for stopping at manager everywhere else
    // is deliberately not followed on either of the two stock reads, because
    // there is no second screen a refused cashier could use.
    //
    // Nothing on this screen writes. The three routes that move a level from the
    // back office — deliveries, corrections, stocktakes — are
    // `@RequireRole('OWNER', 'MANAGER')` and each is its own ticket with its own
    // guard to declare.
    //
    // `order: 95`, immediately after Stock at 90: the levels report a figure and
    // this explains it, so they are two halves of one subject and read left to
    // right in that order. The label is the shorter word because it sits beside
    // Stock and takes its subject from it.
    access: requireSignedIn(),
    nav: { label: 'Movements', order: 95, group: 'STOCK', icon: IconArrowsExchange },
  },

  {
    path: paths.stockCorrections,
    element: <RecordCorrection />,
    // `OWNER` and `MANAGER`, mirroring the API — and **narrower than the two
    // stock reads above it**, which is the direction this table has been careful
    // about in the other direction all the way down. `CorrectionsController`
    // declares `@RequireRole('OWNER', 'MANAGER')` plus `@RequireOutletScope()`;
    // the deliveries and stocktakes routes carry the same, and the two reads
    // carry `@RequireRole()` with no arguments. So the difference here is the
    // API's own and not this screen's caution: reading a shelf is anybody's,
    // changing one is a manager's.
    //
    // There are no till screens in this epic. A terminal moves stock as a side
    // effect of selling and refunding and a cashier never records any, so
    // nothing about this needs a second shell.
    //
    // `order: 100`, after Movements at 95. The levels report a figure, the log
    // explains it, and this is the first of the three acts that change it —
    // deliveries and stocktakes are their own tickets and will want to sit
    // beside it. Whether the five stock entries eventually become one group is a
    // decision for the ticket that can see all of them.
    access: requireRole('OWNER', 'MANAGER'),
    nav: { label: 'Corrections', order: 100, group: 'STOCK', icon: IconPencil },
  },

  {
    path: paths.stockDeliveries,
    element: <RecordDelivery />,
    // `OWNER` and `MANAGER`, mirroring the API and narrower than the two stock
    // reads, for exactly the reasons the Corrections entry above sets out:
    // `DeliveriesController` declares `@RequireRole('OWNER', 'MANAGER')` plus
    // `@RequireOutletScope()`, while `stock-levels` and `stock-movements`
    // declare `@RequireRole()` with no arguments. Reading a shelf is anybody's,
    // changing one is a manager's, and that difference is the API's own rather
    // than this screen's caution.
    //
    // There are no till screens in this epic. A terminal moves stock as a side
    // effect of selling and refunding and a cashier never records any.
    //
    // `order: 97`, between Movements at 95 and Corrections at 100: the levels
    // report a figure, the log explains it, and then come the acts that change
    // it — a delivery first, because it is the one that puts goods on a shelf.
    // Whether the five stock entries eventually become one group is, as that
    // entry says, a decision for the ticket that can see all of them.
    //
    // It is **also** reached from a link in the rail on the Stock screen, shown
    // only to the roles this line admits. That is not a second navigation list:
    // it is the act offered where its own figures are already on the page, and
    // it is what somebody looking at a level that reads short actually wants.
    access: requireRole('OWNER', 'MANAGER'),
    nav: { label: 'Deliveries', order: 97, group: 'STOCK', icon: IconTruckDelivery },
  },

  {
    path: paths.stocktake,
    element: <RecordStocktake />,
    // `OWNER` and `MANAGER`, mirroring the API exactly and by the same argument
    // as the corrections entry above: `StocktakesController.record` declares
    // `@RequireRole('OWNER', 'MANAGER')` plus `@RequireOutletScope()`, while the
    // two stock reads declare `@RequireRole()` with no arguments. The screen
    // writes, so it takes the write's guard.
    //
    // There are no till screens in this epic. A terminal moves stock as a side
    // effect of selling and refunding and a cashier never counts a shelf, so
    // nothing about this needs a second shell.
    //
    // `order: 105`, after Corrections at 100. The levels report a figure, the log
    // explains it, and the acts that change it follow — a correction says what
    // happened to particular goods, a stocktake says what is actually there.
    access: requireRole('OWNER', 'MANAGER'),
    nav: { label: 'Stocktake', order: 105, group: 'STOCK', icon: IconClipboardList },
  },

  {
    path: paths.expenses,
    element: <Expenses />,
    // `OWNER` and `MANAGER`, mirroring the API without relaxing it, and **the
    // first entry of the Reporting section** — three more follow it and take
    // their shape from this one.
    //
    // `ExpensesController.list` declares `@RequireOutletScope()` and no role
    // list at all, so reach is a conjunct of the query rather than a rank. The
    // part worth knowing before this is tightened: `outletScopeFilter` answers
    // `undefined` for an owner *and* for a manager (FR-111 as amended — the
    // requirement that confined a manager to assigned outlets was withdrawn as
    // unimplementable against the identity model), so on this route a manager
    // sees **every** outlet rather than a subset. A guard here narrower than
    // that would hide a screen from somebody the API would have served, and
    // nothing would fail to tell anybody.
    //
    // Stopping at manager rather than following the scope all the way to
    // `requireSignedIn()`, for the reason the catalogue, tickets and sales
    // entries give: outlet and gate staff sign in with a PIN at a counter, and
    // the back-office shell is not their shell. The two stock reads are the
    // deliberate exception and say why on their own entries — there, a refused
    // cashier has no second screen. Here they have nothing to do with expenses.
    //
    // The screen writes nothing; its one action is a link to the form below,
    // which carries the same declaration, so there is no control here to hide
    // from a manager.
    //
    // `order: 110`, opening a band of its own after the five stock entries end
    // at 105. FE050, FE051 and FE052 take 120, 130 and 140 — a gap of ten each,
    // so a screen can be slipped between two without renumbering the row.
    // `navigationFor` sorts on this number alone, and two entries sharing one
    // leaves their order to `sort` stability, which is a link that moves under
    // somebody's pointer between builds.
    access: requireRole('OWNER', 'MANAGER'),
    nav: { label: 'Expenses', order: 110, group: 'MONEY', icon: IconCash },
  },

  {
    path: paths.vendors,
    element: <Vendors />,
    // `OWNER` and `MANAGER`, mirroring `VendorsController`'s class-level
    // `@RequireRole('OWNER', 'MANAGER')`: the list, create, edit and retire all
    // admit both, so nothing on the screen is hidden from a manager.
    //
    // `order: 112`, straight after Expenses: a vendor is who an expense was paid.
    access: requireRole('OWNER', 'MANAGER'),
    nav: { label: 'Vendors', order: 112, group: 'MONEY', icon: IconAddressBook },
  },

  {
    path: paths.upiDeclarations,
    element: <UpiDeclarations />,
    // `OWNER` and `MANAGER`, mirroring all three UPI routes' `@RequireRole`:
    // either may declare, correct and read, so nothing here is hidden.
    //
    // `order: 116`, after Day close: a declaration is a figure for a day, and
    // changes nothing Day close publishes (blossom-rest-api#516).
    access: requireRole('OWNER', 'MANAGER'),
    nav: { label: 'UPI takings', order: 116, group: 'MONEY', icon: IconQrcode },
  },

  {
    path: paths.revenueShares,
    element: <RevenueShares />,
    // `OWNER` and `MANAGER`, mirroring the read. Opening and closing an
    // agreement are `@RequireRole('OWNER')`, so the screen hides both from a
    // manager — courtesy only; it still handles `AUTH_FORBIDDEN` arriving.
    access: requireRole('OWNER', 'MANAGER'),
    nav: { label: 'Revenue shares', order: 118, group: 'MONEY', icon: IconPercentage },
  },

  {
    path: paths.dailySheet,
    element: <DailySheet />,
    // `OWNER` and `MANAGER`, mirroring the sheet, the opening-balance read, the
    // export and `POST /v1/park-expenses`. Recording the opening balance is
    // `@RequireRole('OWNER')` and lives on its own route below.
    //
    // `order: 119`, after UPI takings and Revenue shares: those are what the
    // sheet is built from, and it is what they are reconciled on.
    access: requireRole('OWNER', 'MANAGER'),
    nav: { label: 'Daily sheet', order: 119, group: 'MONEY', icon: IconBook2 },
  },

  {
    path: paths.dailySheetOpeningBalance,
    element: <OpeningBalance />,
    // `OWNER`, mirroring `POST /v1/opening-balances`. A manager reads the
    // balance on the sheet itself and is never linked here. No `nav`, so Daily
    // sheet stays lit.
    access: requireRole('OWNER'),
  },

  {
    path: paths.dailySheetLayout,
    element: <SheetLayout />,
    // `OWNER` and `MANAGER`, mirroring `GET /v1/daily-sheet-layout`. The `PUT`
    // is `@RequireRole('OWNER')`, so a manager gets the layout read-only.
    access: requireRole('OWNER', 'MANAGER'),
  },

  {
    path: paths.recordExpense,
    element: <RecordExpense />,
    // `OWNER` and `MANAGER`, mirroring `POST /v1/outlets/{outletId}/expenses`'s
    // own `@RequireRole('OWNER','MANAGER')` exactly. **The only write in the
    // Reporting epic**, and the same pair as the log it is reached from — so
    // there is nobody who can read the list and not record a row, and nobody
    // who can record one and not see it afterwards.
    //
    // No `nav` entry, and that is what lets this path nest under the log's when
    // the five stock screens deliberately do not nest under theirs. `NavLink`
    // matches descendants: five siblings each carrying an entry would light two
    // at once and announce `aria-current="page"` on both, which is why those are
    // flat. This one carries no entry, so descendant matching is exactly what is
    // wanted — Expenses stays lit while somebody is recording one, which is
    // where they actually are. The same shape as `/catalogue/items/:id/prices`
    // keeping Catalogue lit.
    access: requireRole('OWNER', 'MANAGER'),
  },

  {
    path: paths.tradingSummary,
    element: <TradingSummary />,
    // `OWNER` and `MANAGER`, mirroring the API without relaxing it and without
    // tightening it. `ProfitAndLossController.tradingSummary` declares
    // `@RequireOutletScope()` and **no role list** — the same declaration the
    // expense log carries — so reach is a conjunct of the query rather than a
    // rank, and `outletScopeFilter` answers `undefined` for a manager as well as
    // an owner (FR-111 as amended). A manager therefore reads **every** outlet
    // here, and a guard narrower than that would hide a screen from somebody the
    // API would have served with nothing failing to tell anybody.
    //
    // **Do not infer this entry from the profit and loss beside it.** That route
    // really is `@RequireRole('OWNER')`, and the split between the two is
    // exactly cost and margin: `OutletProfitAndLossResource` carries
    // `costOfGoods` and `OutletTradingSummaryResource` is the same nine fields
    // without it. Mirroring the decorator each route actually declares is the
    // rule; assuming the pair are symmetric is how a manager-readable report
    // becomes owner-only by accident.
    //
    // The consequence for the screen, which is the unusual half: because the
    // resource carries no cost and no margin at the type level, **nothing on
    // this screen is conditionally rendered by role.** Everything the route
    // answers with is safe for anybody who could reach it — that is the property
    // the type split buys, and re-implementing it in the view would be a second,
    // worse copy of a guarantee that already holds.
    //
    // Stopping at manager rather than following the scope all the way to
    // `requireSignedIn()`, for the reason the catalogue, tickets, sales and
    // expenses entries give: outlet and gate staff sign in with a PIN at a
    // counter, and the back-office shell is not their shell.
    //
    // `order: 140`, the number the Expenses entry reserved for FE052. FE050
    // landed on 120 with the cash book above; FE051 takes 130 and is still
    // being built, so this leaves that gap rather than closing up behind it.
    access: requireRole('OWNER', 'MANAGER'),
    nav: { label: 'Trading summary', order: 140, group: 'REPORTS', icon: IconChartBar },
  },

  {
    path: paths.profitAndLoss,
    element: <ProfitAndLoss />,
    // **`requireRole('OWNER')`, and the only entry in the Reporting section that
    // is.** All three routes this screen touches —
    // `ProfitAndLossController.park`, `ProfitAndLossController.outlet` and
    // `ExportController.profitAndLossCsv` — declare `@RequireRole('OWNER')`, and
    // a manager is refused `403 AUTH_FORBIDDEN` at the routing layer before the
    // handler runs. There is no partial response and no field-level filtering,
    // so a manager admitted here would reach a screen that is nothing but a
    // refusal.
    //
    // **Do not infer this entry from the four around it, and do not infer them
    // from this one.** The expense log, the cash book and the trading summary
    // all declare `@RequireOutletScope()` and no role list, which
    // `outletScopeFilter` answers `undefined` for an owner *and* a manager — so
    // those three are `OWNER, MANAGER` and this one is not. The rule behind the
    // split is exactly cost and margin, and `export.controller.ts` says so in
    // those words: `profit-and-loss.csv` is owner-only *because* it carries
    // both. `OutletTradingSummaryResource` is the same nine figures with
    // `costOfGoods` absent at the type level rather than filtered on the way
    // out, and `/trading-summary` is the screen a manager gets instead.
    //
    // This is the direction that fails quietly. `route-audit.ts` throws on a
    // route that declared *nothing*; a route that declared the *wrong role*
    // audits perfectly clean. Declared too narrowly, a screen the API would have
    // served is hidden and nothing says so; declared too widely, a manager is
    // shown a link to a report the server refuses in full — which is the thing
    // `docs/ui-conventions.md` asks navigation not to do.
    //
    // Not narrowed further by outlet, and there is nothing to narrow: the park
    // read reports on every outlet the caller reaches, and an owner reaches all
    // of them.
    //
    // The screen still handles `AUTH_FORBIDDEN` arriving. `role` comes from
    // `sessionStorage` and is attacker-editable, so this line decides what is
    // *drawn* and the server decides what is *permitted*.
    //
    // `order: 130`, the number the Expenses entry reserved for FE051 — between
    // the cash book at 120 and the trading summary at 140. The two reports sit
    // next to each other on purpose: they answer the same question at two
    // different levels of disclosure, and an owner should see which one they are
    // reading.
    access: requireRole('OWNER'),
    nav: { label: 'Profit and loss', order: 130, group: 'REPORTS', icon: IconChartLine },
  },

  {
    path: paths.attribution,
    element: <Attribution />,
    // **`requireRole('OWNER')`, the second entry in this section that names a
    // role.** `AttributionController.staff` and `AttributionController.terminals`
    // both declare `@RequireRole('OWNER')`, and their `403` says so in as many
    // words: *a manager, an outlet staff member and a gate staff member are all
    // refused here, and all read the item-level reports instead* (CL-901).
    // There is no partial response and no field-level filtering, so a manager
    // admitted here would reach a screen that is nothing but a refusal.
    //
    // **Do not infer this from the three `@RequireOutletScope()` entries around
    // it.** The expense log, the cash book and the trading summary carry no role
    // list, and `outletScopeFilter` answers `undefined` for a manager as well as
    // an owner — so those three are `OWNER, MANAGER` and these two routes are
    // not. Mirroring the decorator each route actually declares is the rule.
    //
    // Declared too narrowly, a screen the API would have served is hidden and
    // nothing says so; declared too widely, a manager is shown a link to a
    // report the server refuses in full. `route-audit.ts` throws on a route that
    // declared *nothing* and is silent on one that declared the wrong role,
    // which is why the reasoning is written down rather than inferred.
    //
    // The screen still handles `AUTH_FORBIDDEN` arriving, and has a state for
    // it: `role` comes from `sessionStorage` and is attacker-editable, so this
    // line decides what is *drawn* and the server decides what is *served*.
    //
    // `order: 150`, after the trading summary at 140 and leaving 160 to #187,
    // which is being built against this same section at the same time.
    access: requireRole('OWNER'),
    nav: { label: 'Attribution', order: 150, group: 'REPORTS', icon: IconListNumbers },
  },

  {
    path: paths.trace,
    element: <Trace />,
    // **`requireRole('OWNER', 'MANAGER')`, read off the route's own declaration
    // and not off an outlet scope.** `GET /v1/reports/trace` publishes
    // `AUTH_FORBIDDEN` as "the caller is authenticated but holds none of the
    // roles this route admits (OWNER, MANAGER)", which is a stated list — unlike
    // the expense log, the cash book and the trading summary, which declare
    // `@RequireOutletScope()` and no roles and arrive at the same pair through
    // `outletScopeFilter`. Same two roles, different reasons, and this one does
    // not move if that filter ever does.
    //
    // **It is the only entry in this section whose declared list names
    // `MANAGER`.** The profit and loss above and the attribution ranks below
    // each declare `OWNER` alone. So this is the one Reporting guard that can be
    // neither copied from the three scope-filtered entries nor from the two
    // role-declaring ones — it has to be read off its own route, which is the
    // rule the whole section is written to.
    //
    // **This is the investigation read a manager can reach**, and the split from
    // `profitAndLoss` above holds: that one is owner-only because it carries cost
    // and margin, and there is neither on this route. A guard here copied from it
    // would hide from a manager the one screen that answers "what happened to
    // this receipt", which is a question a manager is asked at a counter.
    //
    // Stopping at manager rather than following on to `requireSignedIn()` for the
    // reason the entries around it give: outlet and gate staff sign in with a PIN
    // at a counter and the back-office shell is not their shell.
    //
    // `order: 160`, after the attribution ranks at 150. That gap was left open
    // while #186 was in flight beside this one rather than closed up behind it,
    // and #186 landed into it — the same courtesy `profitAndLoss` was given at
    // 130 while FE051 was being built.
    access: requireRole('OWNER', 'MANAGER'),
    nav: { label: 'Trace', order: 160, group: 'REPORTS', icon: IconTimeline },
  },

  {
    path: paths.items,
    element: <Items />,
    // **`requireRole('OWNER')`, the third entry in this section that names a
    // role.** `InvestigationController.parkItems` and
    // `InvestigationController.outletItems` both declare `@RequireRole('OWNER')`,
    // and their `403` is explicit that a manager, an outlet staff member and a
    // gate staff member are each refused before the handler runs. There is no
    // partial response and no field-level filtering, so anybody admitted here who
    // is not the owner reaches a screen that is nothing but a refusal.
    //
    // The reason sits in the payload as well as in the decorator: **every row
    // carries `costPaise` and `marginPaise`**, which is the same line
    // `profitAndLoss` at 130 is drawn on. That is the check to make if either
    // route's roles ever move — a read that publishes what something cost is the
    // owner's whatever the decorator currently says.
    //
    // **Do not infer this from `trace` at 160 above it.** That route's own
    // declared list names `MANAGER`, and it is the only one in this section that
    // does; it also carries neither cost nor margin. Nor from the three
    // `@RequireOutletScope()` entries — the expense log, the cash book and the
    // trading summary name no roles at all and reach `OWNER, MANAGER` through
    // `outletScopeFilter`. Each guard here mirrors its own route's declaration
    // and never its neighbour's.
    //
    // The screen still handles `AUTH_FORBIDDEN` arriving, and has a state for
    // it: `role` comes from `sessionStorage` and is attacker-editable, so this
    // line decides what is *drawn* and the server decides what is *served*.
    //
    // `order: 170`, after the trace at 160. #188 is being built against this
    // same section at the same time and owns 180, so this does not reach for it.
    access: requireRole('OWNER'),
    nav: { label: 'Items', order: 170, group: 'REPORTS', icon: IconReportSearch },
  },

  {
    path: paths.itemSales,
    element: <ItemSales />,
    // **`requireRole('MANAGER')`, and narrower than the API on purpose.**
    // `ItemTradingController.park` and `.outlet` admit `OWNER` and `MANAGER`,
    // and outlet staff are refused `403` by the role guard before any row is
    // read. Those routes are the manager-reachable twin of `items` above: the
    // same rows with `costPaise`, `refundedCostPaise`, `voidedCostPaise` and
    // `marginPaise` absent from the *type*, so this screen has no cost to draw.
    //
    // The owner is left out so an owner keeps one entry — Items, which carries
    // everything this does and the cost beside it — rather than two answering
    // one question. That is a navigation choice and not a security line: the
    // server would serve an owner here. Widening this to `OWNER` is safe; what
    // must never happen is `items` above widening to `MANAGER`, because that
    // read's payload carries the cost whatever the screen draws.
    //
    // Stopping at manager rather than `requireSignedIn()` for the reason the
    // entries around it give: outlet and gate staff sign in with a PIN at a
    // counter, the back-office shell is not their shell, and the API refuses
    // them anyway.
    //
    // `order: 172`, beside Items at 170 and before Payment modes at 175. Only
    // one of the two is ever in a given person's navigation.
    access: requireRole('MANAGER'),
    nav: { label: 'Item sales', order: 172, group: 'REPORTS', icon: IconReportSearch },
  },

  {
    path: paths.paymentModes,
    element: <PaymentModes />,
    // `OWNER` and `MANAGER`, the cash book's declaration reached the same way.
    // `GET /v1/reports/payment-modes`, its per-outlet twin and the `.csv` beside
    // them carry `@RequireOutletScope()` and no role list, and
    // `outletScopeFilter` answers `undefined` for an owner *and* a manager.
    //
    // **Do not copy `items` above.** That one is owner-only because every row
    // carries cost and margin; these carry neither — only money taken and
    // handed back per mode, which is what a manager closing a day is asked.
    //
    // Stopping at manager for the reason the cash book gives: outlet and gate
    // staff sign in with a PIN at a counter and this shell is not theirs. The
    // screen still handles `AUTH_FORBIDDEN` arriving.
    //
    // `order: 175`, beside Items at 170 and before Item export at 180.
    access: requireRole('OWNER', 'MANAGER'),
    nav: { label: 'Payment modes', order: 175, group: 'REPORTS', icon: IconCreditCard },
  },

  {
    path: paths.itemExport,
    element: <ItemExport />,
    // **`requireRole('OWNER')`, read off both routes this screen uses.**
    // `ExportController.parkItemsCsv` and `InvestigationController.parkItems`
    // each carry `@RequireRole('OWNER')` and each publish `AUTH_FORBIDDEN` as
    // "none of the roles this route admits (OWNER)". A stated list, like the
    // profit and loss above and unlike the three entries that reach a pair of
    // roles through `outletScopeFilter`.
    //
    // The reason is the same one that splits the epic in `export.controller.ts`:
    // every item row carries `costPaise`, `refundedCostPaise`, `voidedCostPaise`
    // and `marginPaise`, which is the park's buying position per item. The trace
    // screen above admits a manager because there is neither cost nor margin on
    // its route; a guard copied from it would hand a manager this file.
    //
    // The screen still handles `AUTH_FORBIDDEN` arriving, and has a state for
    // it: `role` comes from `sessionStorage` and is attacker-editable, so this
    // line decides what is *drawn* and the server decides what is *served*.
    //
    // `order: 180`, after the items screen at 170. That number was left open
    // while #185 was in flight beside this one rather than claimed, and #185
    // landed into it — the same courtesy `profitAndLoss` was given at 130 and
    // `trace` at 160. The two entries sit next to each other in the margin,
    // which is where a reader of the items screen will look for its export.
    access: requireRole('OWNER'),
    nav: { label: 'Item export', order: 180, group: 'REPORTS', icon: IconFileExport },
  },

  {
    path: paths.dayClose,
    element: <DayClose />,
    // `OWNER` and `MANAGER`, mirroring the two **reads** this screen is built on:
    // `OutletBusinessDaysController.lookup` and `ReconciliationController.read`
    // both publish `AUTH_FORBIDDEN` for "none of the roles this route admits
    // (OWNER, MANAGER)".
    //
    // **It cannot mirror the acts, because the acts disagree with each other.**
    // `BusinessDayCloseController.closeDay` admits OWNER and MANAGER;
    // `reopenDay` beside it admits OWNER alone, because reopening undoes a
    // declaration another person signed off (CL-603, FR-613a). A route declared
    // `requireRole('OWNER')` would hide the whole reconciliation from every
    // manager in the park to protect one button — and closing is a manager's job,
    // so it would hide the screen from the people who use it most. The reopen
    // control is therefore hidden on the screen and refused by the server, which
    // is exactly the split `docs/ui-conventions.md` describes: hiding is
    // courtesy, and `role` comes from `sessionStorage`.
    //
    // Not narrowed by outlet. FR-642c, which confined a manager to their own
    // outlets, was **withdrawn** on 16 Aug 2026 as unimplementable against the
    // identity model and recorded as an accepted risk — `outletScopeFilter`
    // answers `undefined` for an owner *and* for a manager, so on these routes a
    // manager reaches every outlet's day. The same finding the Expenses entry
    // above records. A guard narrower than that hides a screen from somebody the
    // API would have served, and nothing fails to tell anybody.
    //
    // Stopping at manager rather than `requireSignedIn()`, for the reason the
    // catalogue, tickets, sales and expenses entries give: outlet and gate staff
    // sign in with a PIN at a counter and the back-office shell is not theirs.
    // The till counts its own drawer at the end of a shift, which is a different
    // act on a different screen.
    //
    // `order: 115`, between Expenses at 110 and the reporting screens that take
    // 120, 130 and 140. Cash-up reads the expense log's figures back, so it sits
    // beside it rather than opening the reporting band. `navigationFor` sorts on
    // this number alone, and two entries sharing one leaves their order to
    // `sort` stability — a link that moves under somebody's pointer between
    // builds.
    access: dayCloseAccess,
    nav: { label: 'Day close', order: 115, group: 'MONEY', icon: IconLock },
  },

  {
    path: paths.cashBook,
    element: <CashBook />,
    // `OWNER` and `MANAGER`, mirroring the API without relaxing it and without
    // tightening it — the same declaration as the expense log above, reached the
    // same way.
    //
    // Both routes this screen reads — `GET /v1/reports/cash-book` and
    // `GET /v1/reports/cash-book.csv` — carry `@RequireOutletScope()` and no
    // role list, so reach is a conjunct of the query rather than a rank, and
    // `outletScopeFilter` answers `undefined` for an owner *and* a manager
    // (FR-111 as amended). A manager therefore sees **every** outlet here.
    //
    // **The CSV is not owner-only and that is the whole rule of this epic**:
    // `export.controller.ts` splits the two exports on cost and margin, and this
    // one carries neither. `profit-and-loss.csv` is `@RequireRole('OWNER')`
    // because it does. A guard here copied from that one would hide a screen
    // from somebody the API would have served, and nothing would fail to say so.
    //
    // Stopping at manager rather than following the scope to `requireSignedIn()`
    // for the reason the catalogue, tickets, sales and expense entries give:
    // outlet and gate staff sign in with a PIN at a counter and the back-office
    // shell is not their shell.
    //
    // `order: 120`, the number FE050 was left in the band Expenses opened at
    // 110 — see that entry for why the gaps are ten.
    access: requireRole('OWNER', 'MANAGER'),
    nav: { label: 'Cash book', order: 120, group: 'MONEY', icon: IconNotebook },
  },

  {
    path: paths.queuedWrites,
    element: <QueuedWrites />,
    // `OWNER` and `MANAGER`. The declaration and the reasoning live in
    // `queued-writes/access.ts`, because the count in the back-office header
    // links here and has to read the same answer the guard does.
    //
    // There is no API decorator to mirror — the screen makes no request, it
    // reads the terminal's own queue over the desktop bridge. An earlier version
    // of this entry drew the wrong conclusion from that and declared
    // `requireSignedIn()`. Nothing to mirror is not the same as nothing to
    // constrain: the rows name staff codes and what those people were doing, and
    // with no request behind the screen there is no server refusal to catch
    // whatever this line lets through.
    //
    // No `nav` entry. It is reached from the count in the header, which appears
    // only where there is something to count — a permanent link to an empty
    // queue in every browser's navigation is a link nobody follows twice.
    access: queuedWritesAccess,
  },

  {
    path: paths.till,
    element: <Till />,
    // **`publicRoute()`, and this is the one entry in the table where that is a
    // considered answer rather than an absence of one.** It is worth reading
    // before it is copied, because the reasoning does not transfer to anything
    // in the back office.
    //
    // Every other route here is decided by `decideAccess` against a session. A
    // till has none: a cashier signs in with a staff code and a PIN, checked in
    // the Electron main process against a roster the device cached, because that
    // check has to work with the line down. There is no server route for it and
    // no token at the end of it — so there is nothing for `requireSignedIn()` to
    // read, and declaring it would send every terminal to a back-office sign-in
    // screen that a cashier has no password for. That is not a stricter guard;
    // it is a screen nobody can open.
    //
    // What admits somebody is therefore stated by the screen and enforced by the
    // main process, not by the router:
    //
    //   1. the desktop bridge exists at all — in a browser it does not;
    //   2. the device holds a live, unrevoked lease naming an outlet;
    //   3. a shift is running, checked against a cached roster and a PIN the
    //      renderer never sees.
    //
    // **None of those is reachable from a browser**, which is what makes the
    // public declaration safe rather than merely convenient. Opened in one, this
    // screen finds no bridge and renders a sentence saying so; it cannot read a
    // catalogue, cannot start a shift and cannot issue a ticket, because all
    // three live behind IPC that a browser has no other end of. And the server
    // refuses regardless — every ticket write needs `x-terminal-lease` and
    // `x-acting-staff-code`, and the lease never leaves the main process.
    //
    // Deny-by-default is satisfied in the sense that matters: the guard is not
    // the thing being relied on, and the thing being relied on cannot be edited
    // from the developer tools the way a `sessionStorage` role can.
    access: publicRoute(),
  },

  {
    path: '/preview',
    element: <Preview />,
    // A scratch theme preview against mock data, deleted once real screens
    // exist. It reads no session and calls no API, so it declares `public`
    // truthfully. It is not extended or wired to anything here — the
    // declaration is the whole of its involvement, because every route needs
    // one and this is a route.
    access: publicRoute(),
  },

  {
    path: '*',
    element: <NotFound />,
    // Genuinely public, not merely undeclared. Sending a mistyped URL to
    // sign-in, and then returning the user to a page that does not exist, is
    // worse than saying it does not exist.
    access: publicRoute(),
  },
];

// Runs when this module is first imported, which is before anything renders and
// which the existing test suite reaches. In development and under test an
// undeclared route throws here; in production it is logged and refused on
// screen. The reasoning for the asymmetry is in `route-audit.ts`.
//
// `DEV` is Vite's compile-time constant, not an environment variable: it is
// substituted at build time and there is nothing for turbo to hash or pass
// through, so the rule that guards cache correctness has nothing to guard here.
// eslint-disable-next-line turbo/no-undeclared-env-vars -- Vite build-time constant, not an env var
auditRouteTable(routeTable, import.meta.env.DEV);
